/**
 * V1 — the worker discards a start the server refused for good.
 *
 * One dialog for both doors to it — the job page's refused-start card and the
 * offline indicator's issue list — so the two can never name different
 * consequences. The lines come from `PendingStart.queuedBehind`, which is
 * counted from the same `queuedBehindStart` selection `discardQueuedAction`
 * removes: the start time, the pending finish (said in the description, since
 * the queue may or may not hold one), and the unsent photos and expenses with
 * their counts. Nothing is removed silently.
 */

import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { workerErrorText } from '../lib/errorText';
import { useFormatters } from '../lib/useFormatters';
import { discardQueuedAction, type PendingStart } from '../services/syncQueue';

import { ConsequenceDialog } from './ConsequenceDialog';

export interface DiscardStartDialogProps {
  open: boolean;
  start: PendingStart;
  /** The day's IANA zone (job / sheet); the legacy site clock when unknown. */
  zone: string | null | undefined;
  onClose: () => void;
}

export function DiscardStartDialog({ open, start, zone, onClose }: DiscardStartDialogProps) {
  const { t } = useTranslation('worker');
  const fmt = useFormatters();
  return (
    <ConsequenceDialog
      open={open}
      destructive
      title={t('jobDetail.startFailed.discardTitle', {
        defaultValue: 'Loobud salvestamata algusest?',
      })}
      consequence={[
        {
          label: t('jobDetail.startFailed.consequenceStart', {
            defaultValue: 'Sellest seadmest kustutatakse algusaeg',
          }),
          value: fmt.dateTime(start.startedAt, zone),
        },
        // Unsent work of that day goes too — named with its count,
        // never removed silently (same selection as the discard).
        ...(start.queuedBehind?.photos
          ? [
              {
                label: t('jobDetail.startFailed.consequencePhotos', {
                  count: start.queuedBehind.photos,
                  defaultValue: 'Selle päeva saatmata fotod',
                }),
                value: fmt.number(start.queuedBehind.photos),
              },
            ]
          : []),
        ...(start.queuedBehind?.expenses
          ? [
              {
                label: t('jobDetail.startFailed.consequenceExpenses', {
                  count: start.queuedBehind.expenses,
                  defaultValue: 'Selle päeva saatmata kulud — need tuleb pärast uuesti lisada',
                }),
                value: fmt.number(start.queuedBehind.expenses),
              },
            ]
          : []),
      ]}
      description={
        <>
          <Box component="span" sx={{ display: 'block' }}>
            {t('jobDetail.startFailed.consequenceFinish', {
              defaultValue:
                'Kui selle päeva lõpetamine ootab saatmist, tühistatakse ka see ja seda ei saadeta.',
            })}
          </Box>
          <Box component="span" sx={{ display: 'block', mt: 1 }}>
            {t('jobDetail.startFailed.consequenceNext', {
              defaultValue: 'Päev tuleb registreerida uuesti või lasta see parandusega lisada.',
            })}
          </Box>
        </>
      }
      confirmLabel={t('jobDetail.startFailed.discard', {
        defaultValue: 'Loobu ja alusta uuesti',
      })}
      onConfirm={() => discardQueuedAction(start.id)}
      onClose={onClose}
      errorText={(err) => workerErrorText(t, err)}
    />
  );
}
